import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Custom hook for handling API loading states and error management
 * @param {Object} options - Configuration options
 * @param {boolean} options.showToastOnError - Whether to show toast notifications for errors (default: true)
 * @param {boolean} options.showToastOnSuccess - Whether to show toast notifications for success (default: false)
 * @param {string} options.defaultErrorMessage - Default error message to show (default: 'An error occurred')
 * @returns {Object} API state and handlers
 */
export const useApiState = (options = {}) => {
  const {
    showToastOnError = true,
    showToastOnSuccess = false,
    defaultErrorMessage = 'An error occurred',
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Clear all errors
  const clearErrors = useCallback(() => {
    setError('');
    setSubmitError('');
  }, []);

  // Clear specific error
  const clearError = useCallback(() => {
    setError('');
  }, []);

  const clearSubmitError = useCallback(() => {
    setSubmitError('');
  }, []);

  // Handle API call with loading state
  const executeApi = useCallback(async (apiCall, options = {}) => {
    const {
      loadingState = 'loading', // 'loading' or 'submitting'
      onSuccess,
      onError,
      successMessage,
      errorMessage,
    } = options;

    try {
      // Set appropriate loading state
      if (loadingState === 'submitting') {
        setIsSubmitting(true);
        setSubmitError('');
      } else {
        setIsLoading(true);
        setError('');
      }

      const result = await apiCall();

      // Handle success
      if (onSuccess) {
        onSuccess(result);
      }

      if (successMessage && showToastOnSuccess) {
        toast.success(successMessage);
      }

      return result;
    } catch (err) {
      console.error('API Error:', err);

      const errorMsg = errorMessage || err?.response?.data?.message || err?.message || defaultErrorMessage;

      // Set appropriate error state
      if (loadingState === 'submitting') {
        setSubmitError(errorMsg);
      } else {
        setError(errorMsg);
      }

      // Show toast notification
      if (showToastOnError) {
        toast.error(errorMsg);
      }

      // Handle error callback
      if (onError) {
        onError(err);
      }

      throw err; // Re-throw to allow caller to handle if needed
    } finally {
      // Reset loading states
      if (loadingState === 'submitting') {
        setIsSubmitting(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [showToastOnError, showToastOnSuccess, defaultErrorMessage]);

  // Convenience method for fetching data (GET operations)
  const fetchData = useCallback((apiCall, options = {}) => {
    return executeApi(apiCall, { loadingState: 'loading', ...options });
  }, [executeApi]);

  // Convenience method for submitting data (POST/PUT operations)
  const submitData = useCallback((apiCall, options = {}) => {
    return executeApi(apiCall, { loadingState: 'submitting', ...options });
  }, [executeApi]);

  return {
    // State
    isLoading,
    isSubmitting,
    error,
    submitError,

    // Methods
    clearErrors,
    clearError,
    clearSubmitError,
    executeApi,
    fetchData,
    submitData,

    // Computed states
    hasError: !!error,
    hasSubmitError: !!submitError,
    isApiInProgress: isLoading || isSubmitting,
  };
};

export default useApiState;
